import { Link } from "@tanstack/react-router";
import { ArrowLeft, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ProjectTeamRole,
  useCreateProjectTeamRole,
  useDeleteProjectTeamRole,
  useProjectTeamRoles,
  useUpdateProjectTeamRole,
} from "@/hooks/use-project-team-roles";

function SystemBadge() {
  return (
    <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
      System
    </span>
  );
}

function AddRoleDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [label, setLabel] = useState("");
  const create = useCreateProjectTeamRole();

  function handleSave() {
    const trimmed = label.trim();
    if (!trimmed) return;
    create.mutate(trimmed, {
      onSuccess: () => {
        setLabel("");
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Project Team Role</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <label htmlFor="role-label" className="block text-sm font-medium text-gray-700 mb-1">
            Role label
          </label>
          <Input
            id="role-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Lead Estimator"
          />
          <p className="text-xs text-gray-400 mt-1">
            A unique key will be generated automatically from this label.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={create.isPending || !label.trim()}>
            {create.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleRow({ role }: { role: ProjectTeamRole }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(role.label);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const update = useUpdateProjectTeamRole();
  const remove = useDeleteProjectTeamRole();

  function save() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === role.label) {
      setEditing(false);
      setDraft(role.label);
      return;
    }
    update.mutate(
      { id: role.id, label: trimmed },
      {
        onSuccess: () => setEditing(false),
        onError: () => setDraft(role.label),
      },
    );
  }

  return (
    <TableRow className="hover:bg-gray-50">
      <TableCell className="text-sm font-mono text-gray-600">{role.key}</TableCell>
      <TableCell className="text-sm text-gray-900">
        {editing ? (
          <Input
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") {
                setDraft(role.label);
                setEditing(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="text-left hover:underline"
            onClick={() => setEditing(true)}
          >
            {role.label}
          </button>
        )}
      </TableCell>
      <TableCell>{role.isSystem && <SystemBadge />}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          disabled={role.isSystem || remove.isPending}
          onClick={() => setConfirmDelete(true)}
          aria-label={`Delete ${role.label}`}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete role?"
          description={`Are you sure you want to delete "${role.label}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onConfirm={() => remove.mutate(role.id)}
        />
      </TableCell>
    </TableRow>
  );
}

export function ProjectTeamRolesPage() {
  const { data, isLoading, isError, error, refetch } = useProjectTeamRoles();
  const [addOpen, setAddOpen] = useState(false);
  const roles = data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin">
            <button
              type="button"
              className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-[#334155] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[#334155]">Project Team Roles</h1>
            <p className="text-sm text-gray-500">
              {!isLoading && `${roles.length} role${roles.length !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Role
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {isError && <ErrorState message={error?.message} onRetry={() => refetch()} />}

      {!isLoading && !isError && roles.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No roles yet — add one to get started.</p>
        </div>
      )}

      {!isLoading && !isError && roles.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Key
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Label
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Type
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((r) => (
                <RoleRow key={r.id} role={r} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddRoleDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
